/**
 * HS classification by voting (`POST /hscodes/classify/`, docs/decisions/hs-duty-tax.md part 2).
 *
 * Several local sources vote (lexical over the official wording, semantic
 * embeddings, moderator-confirmed precedents and, when configured, an AI that
 * may only choose among table codes). The result walks down chapter →
 * heading → subheading and stops where the sources disagree, so a vague
 * description gets a confident chapter or heading instead of a wrong
 * subheading — plus a question that narrows it (`within`).
 *
 * Every code and description shown here comes from the HS table, never from AI.
 * Used by the product form (companies) and the moderator classifier page,
 * where `votes` (each voter's ballot) is also returned.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatPercent } from "../../i18n/format";
import { getApiErrorMessage } from "../../lib/apiError";
import { hsDepthKey, hsReasonKey, isKnownVoter } from "../../lib/hsCodes";
import { useHscodesClassifyCreate } from "../../services/hscodes/generated/hs-codes/hs-codes";
import type { ClassifyRequestRequest, ClassifyResponse } from "../../services/hscodes/generated/model";
import "./HsClassifier.css";

export interface HsPick {
  code: string;
  description: string;
  runId: number | null;
}

type Input = Pick<ClassifyRequestRequest, "name" | "description" | "material" | "use" | "destination">;

const HsClassifierPanel = ({
  input,
  selectedCode,
  onSelect,
  showVotes = false,
  onResult,
}: {
  input: Input;
  selectedCode?: string | null;
  onSelect?: (pick: HsPick) => void;
  showVotes?: boolean;
  onResult?: (result: ClassifyResponse) => void;
}) => {
  const { t } = useTranslation(["trade", "common"]);
  const [within, setWithin] = useState<string | null>(null);
  const [clarification, setClarification] = useState("");
  const [result, setResult] = useState<ClassifyResponse | null>(null);
  const [answer, setAnswer] = useState<string | null>(null);
  const classify = useHscodesClassifyCreate({
    mutation: {
      onSuccess: (r) => {
        setResult(r);
        setAnswer(null);
        onResult?.(r);
      },
    },
  });
  const mutate = classify.mutate;

  const run = (opts: { within?: string | null; clarification?: string } = {}) => {
    const name = input.name.trim();
    if (!name) return;
    const w = opts.within === undefined ? within : opts.within;
    const c = opts.clarification === undefined ? clarification : opts.clarification;
    mutate({
      data: {
        name,
        description: input.description?.trim() || undefined,
        material: input.material?.trim() || undefined,
        use: input.use?.trim() || undefined,
        destination: input.destination?.trim() || undefined,
        clarification: c.trim() || undefined,
        within: w || undefined,
      },
    });
  };

  // Classify once per product name / description (the modal opens with them).
  const lastKey = useRef("");
  useEffect(() => {
    const key = `${input.name}|${input.description ?? ""}`;
    if (!input.name.trim() || key === lastKey.current) return;
    lastKey.current = key;
    setWithin(null);
    setClarification("");
    mutate({
      data: { name: input.name.trim(), description: input.description?.trim() || undefined },
    });
  }, [input.name, input.description, mutate]);

  const pick = (code: string, description: string) => onSelect?.({ code, description, runId: result?.runId ?? null });

  return (
    <div className="hs-panel">
      <p className="hs-panel__explainer">
        <i className="fa-solid fa-scale-balanced" /> {t("classifier.explainer")}
      </p>

      {classify.isPending ? (
        <div className="hs-panel__loading" role="status">
          <i className="fa-solid fa-spinner fa-spin" /> {t("classifier.running")}
        </div>
      ) : null}
      {classify.isError ? (
        <div className="hs-panel__error">{getApiErrorMessage(classify.error, t("classifier.error"))}</div>
      ) : null}

      {result && !classify.isPending ? (
        <>
          <StatusLine result={result} within={within} />

          {result.path.length > 0 ? (
            <ol className="hs-path" aria-label={t("classifier.pathLabel")}>
              {result.path.map((step) => (
                <li
                  key={step.code}
                  className={`hs-path__step ${step.code === result.code ? "hs-path__step--final" : ""}`}
                >
                  <span className="hs-path__depth">{t(`depth.${hsDepthKey(step.depth)}`)}</span>
                  <span className="hs-path__code">{step.code}</span>
                  <span className="hs-path__desc">{step.description}</span>
                  {onSelect ? (
                    <button
                      type="button"
                      className={`hs-panel__pick ${selectedCode === step.code ? "hs-panel__pick--selected" : ""}`}
                      onClick={() => pick(step.code, step.description)}
                    >
                      {selectedCode === step.code ? t("classifier.selected") : t("classifier.useThis")}
                    </button>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : null}

          {result.code ? (
            <div className="hs-confidence">
              <span>{t("classifier.confidence", { value: formatPercent(result.confidence * 100) })}</span>
              <div className="hs-confidence__bar">
                <div className="hs-confidence__fill" style={{ width: `${Math.round(result.confidence * 100)}%` }} />
              </div>
            </div>
          ) : null}

          {result.question ? (
            <div className="hs-question">
              <p className="hs-question__text">
                <i className="fa-regular fa-circle-question" /> {result.question.text}
              </p>
              <div className="hs-question__options">
                {result.question.options.map((o) => {
                  // A chapter/heading answer narrows the next run (`within`); a
                  // subheading answer is already the code, so it is picked.
                  const final = o.code.length >= 6;
                  const chosen = final && (answer === o.code || selectedCode === o.code);
                  return (
                    <button
                      key={o.code}
                      type="button"
                      className={`hs-question__option ${chosen ? "hs-question__option--chosen" : ""}`}
                      onClick={() => {
                        if (final) {
                          setAnswer(o.code);
                          pick(o.code, o.description);
                          return;
                        }
                        setWithin(o.code);
                        run({ within: o.code });
                      }}
                    >
                      <strong>{o.code}</strong> {o.description}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {result.alternatives.length > 0 ? (
            <div className="hs-alts">
              <p className="hs-alts__title">{t("classifier.alternatives")}</p>
              {result.alternatives.map((a) => (
                <div key={a.code} className="hs-alt">
                  <div className="hs-alt__head">
                    <span className="hs-path__code">{a.code}</span>
                    <span className="hs-alt__desc">{a.description}</span>
                    {onSelect ? (
                      <button
                        type="button"
                        className={`hs-panel__pick ${selectedCode === a.code ? "hs-panel__pick--selected" : ""}`}
                        onClick={() => pick(a.code, a.description)}
                      >
                        {selectedCode === a.code ? t("classifier.selected") : t("classifier.useThis")}
                      </button>
                    ) : null}
                  </div>
                  <div className="hs-alt__bar" title={t("classifier.voteShare")}>
                    <div className="hs-alt__fill" style={{ width: `${Math.round(a.score * 100)}%` }} />
                    <span>{formatPercent(a.score * 100)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {showVotes && result.votes ? <VotesTable votes={result.votes} /> : null}
        </>
      ) : null}

      <div className="hs-refine">
        <label htmlFor="hs-clarification">{t("classifier.refineLabel")}</label>
        <div className="hs-refine__row">
          <input
            id="hs-clarification"
            className="hs-refine__input"
            placeholder={t("classifier.refinePlaceholder")}
            value={clarification}
            onChange={(e) => setClarification(e.target.value)}
          />
          <button type="button" className="hs-panel__btn" disabled={classify.isPending} onClick={() => run()}>
            {t("classifier.rerun")}
          </button>
          {within ? (
            <button
              type="button"
              className="hs-panel__btn hs-panel__btn--ghost"
              onClick={() => {
                setWithin(null);
                run({ within: null });
              }}
            >
              {t("classifier.clearWithin", { code: within })}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const StatusLine = ({ result, within }: { result: ClassifyResponse; within: string | null }) => {
  const { t } = useTranslation("trade");
  const depth = hsDepthKey(result.depth);
  if (result.status === "unclassifiable") {
    return <div className="hs-status hs-status--none">{t(`classifier.reason.${hsReasonKey(result.reason)}`)}</div>;
  }
  if (!result.code) {
    return <div className="hs-status hs-status--partial">{t("classifier.status.noAgreement")}</div>;
  }
  const deepest = depth === "subheading" || depth === "national";
  return (
    <div className={`hs-status ${deepest ? "hs-status--ok" : "hs-status--partial"}`}>
      <strong>
        {result.status === "needs_answer"
          ? t("classifier.status.needsAnswer", { depth: t(`depth.${depth}`) })
          : t("classifier.status.classified", { depth: t(`depth.${depth}`) })}
      </strong>{" "}
      {deepest ? t("classifier.deepestHelp") : t("classifier.partialHelp")}
      {within ? ` ${t("classifier.withinNote", { code: within })}` : ""}
      <span className="hs-status__version">{t("classifier.version", { version: result.version })}</span>
    </div>
  );
};

const VotesTable = ({ votes }: { votes: NonNullable<ClassifyResponse["votes"]> }) => {
  const { t } = useTranslation("trade");
  return (
    <div className="hs-votes">
      <p className="hs-alts__title">{t("classifier.votes.title")}</p>
      <table>
        <thead>
          <tr>
            <th>{t("classifier.votes.voter")}</th>
            <th>{t("classifier.votes.weight")}</th>
            <th>{t("classifier.votes.ballot")}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(votes).map(([voter, ballot]) => (
            <tr key={voter}>
              <td>
                <strong>{isKnownVoter(voter) ? t(`voter.${voter}`) : voter}</strong>
              </td>
              <td>{ballot.weight}</td>
              <td>
                {ballot.skipped ? (
                  <span className="hs-votes__muted">{t("classifier.votes.skipped", { why: ballot.skipped })}</span>
                ) : null}
                {ballot.error ? <span className="hs-panel__error">{ballot.error}</span> : null}
                {ballot.votes.map((v, i) => (
                  <span key={v.code} className="hs-votes__vote">
                    {i + 1}. {v.code} <small>({v.score.toFixed(2)})</small>
                  </span>
                ))}
                {!ballot.skipped && !ballot.error && ballot.votes.length === 0 ? (
                  <span className="hs-votes__muted">{t("classifier.votes.noVotes")}</span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default HsClassifierPanel;
